import { useEffect, useState } from 'react'
import { Box } from '@mui/system'
import { Divider, Input, Modal } from 'antd'
import moment, { Moment } from 'moment'
import { GeneralAssembly, UserRole, WarehouseId } from '../../../models/models'
import { toastFailure } from '../../../util/toast'
import { CircularProgress, styled } from '@mui/material'
import '../../tables/table-styles.css'
import {
    getGeneralAssemblyByIdRequest,
    GeneralAssemblyRelation,
} from '../../../api/general-assemblies'
import { OrderedListOutlined } from '@ant-design/icons'
import { getUsersRequest } from '../../../api/users'
import { calculateGeneralAssemblyMinutes, GeneralAssemblyMinutes } from '../../../util/util'
import GeneralAssemblyLinesTable from '../../tables/GeneralAssemblyLinesTable'

const Header = styled('div')`
    gap: 10px;
    align-items: center;
    display: flex;
`

const InputRow = styled('div')`
    margin-bottom: 5px;
    display: flex;
    width: 100%;
    flex-direction: row;
    justify-content: space-around;
`

const RowElement = styled('div')`
    width: 47%;
`

const Title = styled('p')`
    font-size: 20px;
    margin: 0;
`

const Label = styled('label')`
    margin-bottom: 3px;
    display: block;
    font-size: 14px;
    font-weight: 500;
`

interface UpdateGeneralAssemblyModalProps {
    generalAssemblyId: number
    closeModal: () => void
}

enum ModalState {
    NotFound = 'NotFound',
    Error = 'Error',
    Success = 'Success',
    Loading = 'Loading',
}

export default function InspectGeneralAssemblyModal({
    generalAssemblyId,
    closeModal,
}: UpdateGeneralAssemblyModalProps) {
    const [description, setDescription] = useState<string>('')
    const [dateToBeCompleted, setDateToBeCompleted] = useState<Moment | null>(null)
    const [completedAt, setCompletedAt] = useState<Moment | null>(null)
    const [warehouseId, setWarehouseId] = useState<WarehouseId>()
    const [minutes, setMinutes] = useState<GeneralAssemblyMinutes>()
    const [modalState, setModalState] = useState<ModalState>(ModalState.Loading)
    const [generalAssembly, setGeneralAssembly] = useState<GeneralAssembly>()

    useEffect(() => {
        getGeneralAssembly()
    }, [])

    const getGeneralAssembly = async () => {
        const response = await getGeneralAssemblyByIdRequest(generalAssemblyId, {
            joins: [
                GeneralAssemblyRelation.CreatedBy,
                GeneralAssemblyRelation.Lines,
                GeneralAssemblyRelation.LinesAssembledBy,
                GeneralAssemblyRelation.Bom,
            ],
        })

        if (response.successful) {
            const generalAssembly = response.data
            setGeneralAssembly(generalAssembly)
            setDescription(generalAssembly.description)
            setCompletedAt(generalAssembly.completedAt ? moment(generalAssembly.completedAt) : null)
            setWarehouseId(generalAssembly.warehouseId)
            setDateToBeCompleted(
                generalAssembly.dateToBeCompletedBy
                    ? moment(generalAssembly.dateToBeCompletedBy)
                    : null
            )
            setMinutes(calculateGeneralAssemblyMinutes(generalAssembly))
            setModalState(ModalState.Success)
        } else {
            if (response.status === 404) {
                setModalState(ModalState.NotFound)
            } else {
                setModalState(ModalState.Error)
            }
            toastFailure(response.message)
        }
    }

    return (
        <Modal open={true} footer={null} onCancel={closeModal}>
            {modalState === ModalState.Loading && (
                <Box
                    sx={{
                        display: 'flex',
                        flexDirection: 'column',
                        gap: '20px',
                        margin: '20px',
                        width: '100%',
                        height: '500px',
                        justifyContent: 'center',
                        alignItems: 'center',
                    }}
                >
                    <CircularProgress size={75} style={{ color: 'rgb(53, 0, 160)' }} />
                </Box>
            )}
            {modalState === ModalState.Error && <Box>Something went wrong</Box>}
            {modalState === ModalState.NotFound && (
                <Box>Could not find the general assembly with the id of {generalAssemblyId}</Box>
            )}
            {modalState === ModalState.Success && (
                <Box>
                    <Header>
                        <OrderedListOutlined style={{ fontSize: '20px' }} />
                        <Title>Inspect General Assembly {generalAssemblyId}</Title>
                    </Header>
                    <Divider />
                    <div>
                        <Label>Description</Label>
                        <Input.TextArea
                            value={description}
                            readOnly={true}
                            onChange={(e) => setDescription(e.target.value)}
                            style={{ minHeight: 100 }}
                        />
                    </div>
                    <InputRow>
                        <RowElement>
                            <Label>Date to be completed</Label>
                            <Input
                                style={{ width: '100%' }}
                                readOnly={true}
                                value={moment(dateToBeCompleted).format('DD/MM/YYYY')}
                            />
                        </RowElement>
                        <RowElement>
                            <Label>Completed Date</Label>
                            <Input
                                style={{ width: '100%' }}
                                readOnly={true}
                                value={
                                    completedAt
                                        ? moment(completedAt).format('DD/MM/YYYY')
                                        : 'Not completed'
                                }
                            />
                        </RowElement>
                    </InputRow>
                    <InputRow>
                        <RowElement>
                            <Label>Warehouse</Label>
                            <Input
                                style={{ width: '100%' }}
                                readOnly={true}
                                value={
                                    [
                                        { value: WarehouseId.Monument, label: 'Monument' },
                                        { value: WarehouseId.Scandia, label: 'Scandia' },
                                        { value: null, label: 'None' },
                                    ].find((option) => option.value === warehouseId)?.label ||
                                    'None'
                                }
                            />
                        </RowElement>
                    </InputRow>
                    <InputRow>
                        <RowElement>
                            <Label>BOM</Label>
                            <Input
                                style={{ width: '100%' }}
                                readOnly={true}
                                value={generalAssembly?.builtItem?.bomCode || 'Could not determine'}
                            />
                        </RowElement>
                        <RowElement>
                            <Label>Minutes completed</Label>
                            <Input
                                style={{ width: '100%' }}
                                readOnly={true}
                                value={`${minutes?.completed}/${minutes?.total}`}
                            />
                        </RowElement>
                    </InputRow>
                    {generalAssembly?.lines && (
                        <>
                            <Label>Lines</Label>
                            <GeneralAssemblyLinesTable datasource={generalAssembly.lines} />
                        </>
                    )}
                </Box>
            )}
        </Modal>
    )
}
